import { Div, Text } from "atomize"
import { useContext, useEffect, useState } from "react"
import { AnimatePresence, motion, useTransform } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { canEditLesson, getLessonColor } from "../utils/func.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import {
	formatMinutes,
	getLessonPlayersText,
	lessonEndMinutes,
	lessonStartMinutes,
} from "../utils/week.utils"
import { MorphLayers, useLerp, useMorphMode } from "../week/viewMorph"
import WeekCourtPill from "../week/WeekCourtPill"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import CardInset from "./CardInset"
import Paragraph from "../atoms/Paragraph"
import LessonHeader from "./LessonHeader"
import LessonBody from "./LessonBody"
import LessonFooter from "./LessonFooter"
import LessonPresence from "./LessonPresence"
import LessonActions from "./LessonActions"
import LessonContent from "./LessonContent"
import LessonAuthorship from "./LessonAuthorship"
import SwipeActions from "./SwipeActions"
import LessonObjective from "./LessonObjective"
import { JustFinishedStub } from "./JustFinished"

// ──────────────────────────────────────────────────────────────────────────
// UNA LEZIONE, SCHEDA O RIGA
//
// Sul telefono la stessa lezione è la scheda del giorno — orario e tipo in
// cima, chi c'è, il campo, e toccandola presenze e bottoni — e la riga della
// settimana: orario, chi c'è, campo, in una riga sola. Nel passaggio non si
// sostituisce l'una con l'altra: la scheda perde il respiro sopra e sotto,
// quello che sta sotto l'intestazione si richiude, e l'intestazione diventa
// quella della riga. Si stringe verso il suo centro, e al contrario si
// riapre. Quanto, lo dice `progress` (vedi week/viewMorph).
//
// Ferma sul giorno è la scheda di sempre (come Lesson, che resta per il
// computer); ferma sulla settimana è la riga, e toccandola si va al suo
// giorno.
// ──────────────────────────────────────────────────────────────────────────

// Quello che scheda e riga hanno in comune, e che nel passaggio vola dal suo
// posto nell'una al suo posto nell'altra (vedi viewMorph): l'orario, chi
// c'è, il campo. Il resto della scheda svanisce, il resto della riga
// compare. Il tipo resta tra questi: scendendo dall'angolo in alto a destra
// a sotto i nomi incrocerebbe i nomi che salgono.
const FLYING_PIECES = ["time", "names", "court"]

function MorphingLesson({
	lesson,
	progress,
	joins,
	isFirst,
	isOpen,
	onToggleOpen,
	onRowClick,
	justFinishedLabel,
	overlapsWith,
}) {
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)
	const mode = useMorphMode(progress)

	const [isLayoutOpen, setIsLayoutOpen] = useState(false)
	// come nella scheda: aperta subito, chiusa con un attimo di ritardo
	useEffect(() => {
		if (isOpen) {
			setIsLayoutOpen(true)
			return
		}
		const timeout = setTimeout(() => setIsLayoutOpen(false), 50)
		return () => clearTimeout(timeout)
	}, [isOpen])

	const lessonType = getLessonType(lesson.lessonType)
	const color = getLessonColor(lessonType)
	const canEdit = canEditLesson(coach, selectedSchool, lesson)
	const isCancelled = isCancelledLesson(lesson)

	// Il giorno attacca due schede consecutive a 4px e le stacca di 16; la
	// settimana a 3 e 10, e il primo giorno non ha margine sopra.
	const { joinsPrev, joinsNext } = joins
	const marginTop = useLerp(progress, joinsPrev ? 4 : 16, isFirst ? 0 : joinsPrev ? 3 : 10)
	const marginBottom = useLerp(progress, joinsNext ? 4 : 16, 0)
	const paddingY = useLerp(progress, 12, 0)
	// dentro, la scheda stringe un po' (vedi lessonCardText); la riga no
	const innerPaddingY = useLerp(progress, 6, 8)
	const paddingX = useLerp(progress, 16, 12)
	const topCorner = useLerp(progress, joinsPrev ? 5 : 20, joinsPrev ? 4 : 15)
	const bottomCorner = useLerp(progress, joinsNext ? 5 : 20, joinsNext ? 4 : 15)
	const corners = useTransform(
		[topCorner, bottomCorner],
		([top, bottom]) => `${top}px ${top}px ${bottom}px ${bottom}px`,
	)
	// mentre la si trascina si stacca tutta
	const wholeCorner = useTransform(progress, t => `${20 - 5 * t}px`)

	// L'ultima finita senza note ha il biglietto in fondo (vedi JustFinished).
	// Resta in fondo anche aperta: la parte aperta si srotola sopra di lui,
	// invece di farlo sparire e ricomparire. Solo nel giorno: nella
	// settimana è una riga, e lì non sta.
	const hasStub = Boolean(justFinishedLabel) && mode === "day"

	const handleClick = () => {
		if (mode === "day") onToggleOpen?.()
		else if (mode === "week") onRowClick?.()
	}

	return (
		<motion.div style={{ marginTop, marginBottom }}>
			{/* nel mezzo del passaggio non si trascina niente */}
			<SwipeActions lessonData={lesson} canEdit={canEdit && mode !== "moving"}>
				{isDragging => (
					<motion.div
						style={{
							background: color,
							borderRadius: isDragging ? wholeCorner : corners,
							paddingTop: paddingY,
							// il biglietto arriva fino al bordo, e gli angoli lo tagliano
							paddingBottom: hasStub ? 0 : paddingY,
							overflow: hasStub ? "hidden" : undefined,
							boxShadow: theme.shadows.accent,
							// annullata: resta in elenco, ma si vede che non si fa
							...(isCancelled && { opacity: 0.55, filter: "grayscale(1)" }),
						}}>
						<motion.div
							style={{
								paddingTop: innerPaddingY,
								paddingBottom: innerPaddingY,
								paddingLeft: paddingX,
								paddingRight: paddingX,
								cursor: "pointer",
							}}
							onClick={handleClick}>
							<MorphLayers
								progress={progress}
								mode={mode}
								flying={FLYING_PIECES}
								week={<LessonRow lesson={lesson} />}
								day={
									<>
										<LessonHeader lessonData={lesson} overlapsWith={overlapsWith} />
										<LessonBody
											lessonData={lesson}
											isOpen={isOpen}
											isLayoutOpen={isLayoutOpen}
											modalView={false}
											sharedLayout={false}
										/>
										{!isOpen && !isCancelled && <LessonObjective lessonData={lesson} />}
										<LessonFooter lessonData={lesson} />
										{/* perché è saltata, anche a lezione chiusa */}
										{isCancelled && lesson.lessonNotes && !isOpen && (
											<Text
												textSize="caption"
												textColor={theme.colors.gunmetal}
												m={{ t: "0.25rem" }}>
												{lesson.lessonNotes}
											</Text>
										)}
									</>
								}
							/>
						</motion.div>

						<AnimatePresence>
							{mode === "day" && isOpen && (
								<motion.div
									transition={{ ease: "easeInOut" }}
									initial={{ height: 0, y: -15 }}
									animate={{ height: "auto", y: 0 }}
									exit={{ height: 0, y: -15 }}
									style={{ overflow: "hidden" }}>
									{!isCancelled && <LessonPresence lessonData={lesson} />}
									<LessonContent lessonData={lesson} canEdit={canEdit} />
									{lesson.lessonNotes && (
										<CardInset>
											<Paragraph m={{ t: "0.75rem" }} title="Note:">
												{lesson.lessonNotes}
											</Paragraph>
										</CardInset>
									)}
									<LessonActions lessonData={lesson} canEdit={canEdit} />
									<LessonAuthorship lessonData={lesson} />
								</motion.div>
							)}
						</AnimatePresence>

						{hasStub && <JustFinishedStub lessonData={lesson} label={justFinishedLabel} />}
					</motion.div>
				)}
			</SwipeActions>
		</motion.div>
	)
}

// La riga della settimana: orario, chi c'è con la tipologia sotto, il campo
function LessonRow({ lesson }) {
	const { getLessonType } = useContext(sharedDataContext)
	const lessonType = getLessonType(lesson.lessonType)
	const players = getLessonPlayersText(lesson)
	const isCancelled = isCancelledLesson(lesson)
	const typeLabel = (
		<>
			<LessonTypeLabel
				lessonTypeID={lesson.lessonType}
				lessonType={lessonType}
				background={getLessonColor(lessonType)}
			/>
			{isCancelled && " · Annullata"}
		</>
	)

	return (
		<Div d="flex" align="center" style={{ gap: "0.75rem" }}>
			<Text
				data-morph="time"
				textWeight="bold"
				textSize="caption"
				textColor={theme.colors.gunmetal}
				style={{
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
					...(isCancelled && { textDecoration: "line-through" }),
				}}>
				{formatMinutes(lessonStartMinutes(lesson))}–{formatMinutes(lessonEndMinutes(lesson))}
			</Text>
			<Div flexGrow="1" style={{ minWidth: 0 }}>
				<Text
					data-morph={players ? "names" : undefined}
					textSize="caption"
					textColor={theme.colors.gunmetal}
					style={{
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}>
					{players || typeLabel}
				</Text>
				{players && (
					<Text
						textSize="tiny"
						textColor={getLessonColor(lessonType, true)}
						style={{ whiteSpace: "nowrap" }}>
						{typeLabel}
						{lesson.recurrencyID && (
							<>
								{" · "}
								<FontAwesomeIcon icon={faSyncAlt} size="xs" />
							</>
						)}
					</Text>
				)}
			</Div>
			<WeekCourtPill lesson={lesson} />
		</Div>
	)
}

export default MorphingLesson
